import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Subject, filter, takeUntil } from 'rxjs';
import { HlmToaster } from '@spartan-ng/helm/sonner';
import { _Service } from '@core/services/_.service';
import { SeoService } from '@core/services/seo.service';
import { ContactService } from '@core/services/contact.service';

@Component({
  selector: 'app-root',
  styles: [
    `
      :host ::ng-deep [data-sonner-toast] {
        background-color: #1c1b22 !important;
        border: 1px solid hsl(var(--border)) !important;
        color: hsl(var(--foreground)) !important;
      }
    `,
  ],
  template: `
    <router-outlet />
    <hlm-toaster
      position="bottom-right"
      [offset]="16"
      [toastOptions]="{
        classes: {
          toast: 'bg-card border border-border text-foreground shadow-lg'
        }
      }" />
  `,
  imports: [RouterOutlet, HlmToaster],
})
export class AppComponent implements OnInit, OnDestroy {
  private _ = inject(_Service);
  private router = inject(Router);
  private seo = inject(SeoService);
  private contact = inject(ContactService);

  title = 'Ulabase';

  destroyed = new Subject<void>();

  // Application/auth areas that must never be indexed. Kept in sync with robots.txt.
  private readonly nonIndexablePrefixes = [
    '/auth',
    '/oauth',
    '/orgs',
    '/org',
    '/srvs',
    '/me',
    '/admin',
    '/mgmt',
    '/new-service',
    '/accept-terms',
    '/invitations',
    '/signin',
    '/signup',
  ];

  ngOnInit(): void {
    // Language set to English by default
    // Initialize analytics tracking
    this._.init();

    // Emit `noindex` for app/auth routes. Public pages set their own indexable
    // metadata via SeoService when they load.
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this.destroyed)
      )
      .subscribe(e => {
        const path = e.urlAfterRedirects.split(/[?#]/)[0];
        const isNonIndexable = this.nonIndexablePrefixes.some(
          prefix => path === prefix || path.startsWith(`${prefix}/`)
        );
        if (isNonIndexable) {
          this.seo.setNoindex();
        }

        // ?contact=sales opens the form to sales, on any page. Support is about a service, and an
        // older link with ?contact=support leads to the organization's Support page, which lists them
        const topic = new URLSearchParams(e.urlAfterRedirects.split('?')[1]?.split('#')[0] ?? '').get('contact');
        if (topic === 'sales') {
          this.router.navigate([], { queryParams: { contact: null }, queryParamsHandling: 'merge', replaceUrl: true });
          this.contact.open({ topic, subject: '', title: 'Write to us' });
        } else if (topic === 'support') {
          this.router.navigate(['/orgs/active/support'], { replaceUrl: true });
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }
}
